import { useCallback } from 'react';

import { ApiParams } from '../../common/api/api';
import {
  ApiResult,
  FetchApiCallback,
  useApiGet,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { useDownloadBlob } from '../../common/blob.hook';
import { Template, TemplateType } from './Template.interface';

export function useFindTemplates(): [ApiResult<Template[]>, FetchApiCallback] {
  const { get, ...result } = useApiGet<Template[]>('templates');
  return [result, get];
}

export function useCheckIfHasTemplate(
  type: TemplateType
): [ApiResult<boolean>, FetchApiCallback] {
  const { get, ...result } = useApiGet<boolean>(`templates/${type}/template`);
  return [result, get];
}

export function useCreateTemplate(): [
  ApiResult<Template>,
  (dto: Partial<Template>) => void
] {
  const { post, ...result } = useApiPost<Template>('templates');
  const createTemplate = useCallback(
    (dto: Partial<Template>) => {
      post(dto);
    },
    [post]
  );
  return [result, createTemplate];
}

export function useUpdateTemplate(
  id: number
): [ApiResult<Template>, (dto: Partial<Template>) => void] {
  const { patch, ...result } = useApiPatch<Template>(`templates/${id}`);
  const updateTemplate = useCallback(
    (dto: Partial<Template>) => {
      patch(dto);
    },
    [patch]
  );
  return [result, updateTemplate];
}

export function useDownloadTemplate(
  type: TemplateType
): [ApiResult<any>, FetchApiCallback, (fileName: string) => void] {
  const { get, ...result } = useApiGet<any>(`templates/${type}/download`);
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (params?: ApiParams) => {
      // remove old document
      setBlob(undefined);
      get(params);
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}
